import React, { useMemo } from "react";
import { Alert, Button, Empty, Flex, Space, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import FieldRow from "./FieldRow";
import FieldGroup from "./FieldGroup";
import { FIELD_TYPES, newField } from "../../../../../../domain/clinicalForms";
import { useFieldError } from "./ValidationContext";

const FieldError = ({ message, indent = 0 }) =>
  message ? (
    <Typography.Text
      type="danger"
      style={{
        fontSize: 12,
        display: "block",
        marginLeft: indent,
        marginTop: 2,
      }}
    >
      {message}
    </Typography.Text>
  ) : null;

// Walk the flat fields array and group each SECTION_HEADER with the
// consecutive non-header fields that follow it until the next header.
const buildLayout = (fields) => {
  const items = [];
  let current = null;
  fields.forEach((field, idx) => {
    if (field.type === FIELD_TYPES.SECTION_HEADER) {
      current = {
        kind: "group",
        header: field,
        headerIdx: idx,
        children: [],
      };
      items.push(current);
    } else if (current) {
      current.children.push({ field, idx });
    } else {
      items.push({ kind: "field", field, idx });
    }
  });
  return items;
};

const SectionEditor = ({ section, onChange, basePath = [] }) => {
  const titleError = useFieldError([...basePath, "title"]);
  const fieldsError = useFieldError([...basePath, "fields"]);
  const layout = useMemo(() => buildLayout(section.fields), [section.fields]);

  const updateField = (fieldId, next) =>
    onChange({
      ...section,
      fields: section.fields.map((f) => (f.id === fieldId ? next : f)),
    });

  const deleteField = (fieldId) =>
    onChange({
      ...section,
      fields: section.fields.filter((f) => f.id !== fieldId),
    });

  const duplicateField = (fieldId) => {
    const target = section.fields.find((f) => f.id === fieldId);
    if (!target) return;
    const copy = { ...target, id: `${target.id}_copy_${Date.now()}` };
    const idx = section.fields.findIndex((f) => f.id === fieldId);
    const next = section.fields.slice();
    next.splice(idx + 1, 0, copy);
    onChange({ ...section, fields: next });
  };

  const addField = () => {
    fieldsError.clear();
    onChange({ ...section, fields: [...section.fields, newField()] });
  };

  const addHeader = () => {
    fieldsError.clear();
    onChange({
      ...section,
      fields: [...section.fields, newField(FIELD_TYPES.SECTION_HEADER)],
    });
  };

  const titleBanner = titleError.error ? (
    <Alert
      type="error"
      showIcon
      message={titleError.error}
      description="Rename the section via its tab above."
      style={{ marginBottom: 8 }}
    />
  ) : null;

  if (section.fields.length === 0) {
    return (
      <div>
        {titleBanner}
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: fieldsError.error ? "#ff4d4f" : "#98a2b3" }}>
              {fieldsError.error ||
                "No content yet — start by adding a field or group"}
            </span>
          }
        >
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={addHeader}>
              Add group
            </Button>
            <Button icon={<PlusOutlined />} onClick={addField}>
              Add field
            </Button>
          </Space>
        </Empty>
      </div>
    );
  }

  const renderField = (field, idx) => {
    const fieldPath = [...basePath, "fields", idx];
    return (
      <FieldRow
        key={field.id}
        field={field}
        basePath={fieldPath}
        onChange={(next) => updateField(field.id, next)}
        onDelete={() => deleteField(field.id)}
        onDuplicate={() => duplicateField(field.id)}
      />
    );
  };

  return (
    <Flex vertical gap={12}>
      {titleBanner}
      {layout.map((item) => {
        if (item.kind === "field") {
          return renderField(item.field, item.idx);
        }
        const headerPath = [...basePath, "fields", item.headerIdx];
        return (
          <FieldGroup
            key={item.header.id}
            header={item.header}
            headerPath={headerPath}
            onChangeHeader={(next) => updateField(item.header.id, next)}
            onDeleteHeader={() => deleteField(item.header.id)}
          >
            {item.children.length > 0 &&
              item.children.map(({ field, idx }) => renderField(field, idx))}
          </FieldGroup>
        );
      })}

      <FieldError message={fieldsError.error} />

      <Flex gap={8}>
        <Button icon={<PlusOutlined />} onClick={addField}>
          Add field
        </Button>
        <Button type="dashed" icon={<PlusOutlined />} onClick={addHeader}>
          Add group
        </Button>
      </Flex>
    </Flex>
  );
};

export default SectionEditor;
